import * as React from "react";
import { useContext, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import Box from '../../../parts/Box';
import { Helmet } from "react-helmet";
import moment from "moment";
import * as Messages from "../../../Messages";
import { getApiUrl } from "../../../api";
import { JobControllerApi, JobInfo } from "../../../api/openapi-generator";
import { JobInfoContext, JobInfoContextProvider } from "../../../job-info-context";
import { JobContextProvider, useJobContext } from "../context/job-context";
import PrintButton from "./print-button";
import JobContentsComponent from "./JobContentsComponent";
import { useParams } from "react-router-dom";
import { exampleJob } from "../../../settings";
import { ResultsContextProvider } from "../context/results-context";
import ReportSections from "./ReportSections";
import { VinaJobDesiredRunSwitch } from "./vina-job-desired-run-switch";
import { VinaJobDesiredRunContextProvider } from "../context/vina-job-desired-run-context";

interface IRefs {
  asideLeftRef: React.RefObject<HTMLDivElement>,
  beforeAsideLeftRef: React.RefObject<HTMLDivElement>,
}

type IProps = IRefs;

function InhibitorButton({id, download}: {id: string, download: boolean}) {
  const [url, setUrl] = useState<string>("");
  useEffect(() => {
    getApiUrl(JobControllerApi, 'inhibitorsTsvRaw', {id, download}).then(setUrl);
  }, [id, download]);
  return (
    <div>
      <a
        href={url}
        className="btn btn-secondary"
        title="Download inhibitor table in the TSV (tab-separated values) format to import it into Microsoft Excel"
      >Inhibitors (TSV)</a>
    </div>
  )
}

function JobComponent({beforeAsideLeftRef, asideLeftRef}: IRefs) {

  const {
    id,
    jobInfo: detail,
    fetchState: jobInfoFetchState,
  } = useContext(JobInfoContext);


  const {
    uniprotNullable: uniprot,
    backendResultsNullable: backendResults,
    uniprotFetchState,
    backendResultsFetchState,
  } = useJobContext();

  const jobId = id;

  const notFound = uniprotFetchState.errors.clientError?.status === 404 || backendResultsFetchState.errors.clientError?.status === 404;
  const loading = uniprotFetchState.fetching || backendResultsFetchState.fetching;
  const loadFailed = Object.values(uniprotFetchState.errors).length > 0 || Object.values(backendResultsFetchState.errors).length > 0;

  const jobInfoScreenBox = (
    <Box title="Job information">
      <JobInfoScreen id={id} detail={detail}/>
      {(detail && ![-1, 5].includes(detail.status)) ? (
        Messages.JOB_ONGOING()
      ) :
      jobInfoFetchState.fetching ? (
          Messages.JOB_INFO_FETCHING()
      ) :
      jobInfoFetchState.errors.clientError?.status === 404 ? (
        Messages.JOB_INFO_NOT_FOUND(jobId)
      ) :
      Object.values(jobInfoFetchState.errors).length > 0 ? (
        Messages.JOB_INFO_FAILED(jobId)
      ) : null}
    </Box>
  )

  return <div>

    <Helmet>
      <title>{`Job ${jobId}`}</title>
    </Helmet>

    {beforeAsideLeftRef.current && (
      createPortal((
        <div className="ll-section ll-print">
          <div className="ll-section-header" style={{ padding: '5px' }}>
            <div className="ll-section-name" style={{ fontSize: '1.2rem' }}>
              <span>Job information</span>
            </div>
          </div>
          <div className="ll-section-body">
            <JobInfoPrint detail={detail}/>
          </div>
        </div>
      ), beforeAsideLeftRef.current)
    )}

    <>
      {backendResults && uniprot ? (
        <ResultsContextProvider backendResults={backendResults} uniprot={uniprot}>
          <div className="ll-box-group ll-screen">
            {jobInfoScreenBox}
            <Box title="Download results" className="ll-download-results">
              <div className="ll-vertical-button-group">
                <div>
                  <PrintButton all containerStyle={{ width: 'max-content' }} />
                </div>
                <div>
                  <InhibitorButton id={id} download={true}/>
                </div>
              </div>
            </Box>
          </div>
          <ReportSections/>
          {asideLeftRef.current && (
            createPortal(<JobContentsComponent />, asideLeftRef.current)
          )}
        </ResultsContextProvider>
      ) : (
        <div className="ll-box-group ll-screen">
          {jobInfoScreenBox}
        </div>
      )}

      {notFound && (
        <div className="ll-result-message">
          {Messages.JOB_RESULTS_NOT_FOUND()}
        </div>
      )}
      {loading && (
        <div className="ll-result-message">
          {Messages.JOB_RESULTS_LOADING()}
        </div>
      )}
      {loadFailed && (
        <div className="ll-result-message">
          {Messages.JOB_RESULTS_FAILED(jobId)}
        </div>
      )}
    </>

  </div>
}


function JobInfoScreen({id, detail}: {id: string, detail: JobInfo | null}) {
  return (
    <table className="ll-info-table">
      <thead>
      <tr>
        <th>ID:</th>
        <td>{id}</td>
      </tr>
      {detail && <>
        <tr>
          <th>Title:</th>
          <td>{detail.title}</td>
        </tr>
        <tr>
          <th>Time:</th>
          <td>{detail.time}</td>
        </tr>
        <tr>
          <th>Status:</th>
          {/* <td>{jobInfo.inQueue ? "In queue" : jobStatusNames[jobInfo.status]}</td> */}
            <td>{
              detail.status === -1 ? 'Failed' :
                detail.status === 5  ? <strong>Done</strong> :
                  /* else */   'In progress...'
            }</td>
        </tr>
        <tr>
          <th>Analyze inhibitors:</th>
          <td><VinaJobDesiredRunSwitch/></td>
        </tr>
      </>}
      </thead>
    </table>
  )
}

function JobInfoPrint({detail}: {detail: JobInfo | null}) {
  if (!detail) return null;
  return (
    <table className="ll-info-table" style={{ width: 'auto' }}>
      <thead>
      <tr>
        <th>ID:</th>
        <td>{detail.id}</td>
      </tr>
      <tr>
        <th>Title:</th>
        <td>{detail.title}</td>
      </tr>
      <tr>
        <th>Job submitted:</th>
        <td>{detail.time}</td>
      </tr>
      <tr>
        <th>Results accessed:</th>
        <td>{moment().format('DD MM YYYY HH:mm')}</td>
      </tr>
      <tr>
        <th>Results link:</th>
        <td><a href={`${window.location.href}`}>{window.location.href}</a></td>
      </tr>
      </thead>
    </table>
  )
}

function JobComponentWrapper(props: IProps) {
  const params = useParams<{jobId: string}>();
  let id = params['jobId'];
  if (id === exampleJob.jobIdAlias) {
    id = exampleJob.jobId;
  }
  return (
    <JobInfoContextProvider id={id}>
      <JobContextProvider>
        <VinaJobDesiredRunContextProvider>
          <JobComponent {...props} />
        </VinaJobDesiredRunContextProvider>
      </JobContextProvider>
    </JobInfoContextProvider>
  )
}

export default JobComponentWrapper;
